import { LogSet } from "./schemas/logSet.js";
import { Session } from "./schemas/session.js";
import {
  buildCursorPath,
  paginate,
  type AbortLike,
  type FetchPage,
} from "./asyncList.js";

// PG-38: Progress's "All" stat read a single `/api/sessions?limit=200` and
// showed `sessions.length` under the "All" label. At 1,041 real sessions
// that was 200, off by 5x: a truth defect (⭐ per data-volume.md), not a
// performance one. Either the number is the real total or the label says
// what it's counting.
//
// Deliberately its OWN module rather than a mode of the recency lists
// (WorkoutsView's month sections, useChartWorkouts): data-volume.md's PG-42
// note is that a list and a total want OPPOSITE cures. A list wants the
// newest N fast, with older pages reachable on scroll; truncating past
// page one is fine, that's what "load more" is for. A total wants no
// truncation at all: it must walk every page, however many that takes, or
// stop calling itself a total. Sharing one fetchPage between the two shapes
// would either slow the list down to satisfy the total, or quietly cap the
// total to satisfy the list (the PG-40/HC-22 mistake again). They don't
// share a hook on purpose.

/** Server clamp: apps/api/src/routes/sessions.ts:210 caps `?limit=` at 200
 *  no matter what's asked, so requesting the cap itself keeps this to the
 *  fewest possible round-trips. */
export const SESSIONS_ALL_PAGE_SIZE = 200;

/** Page-count ceiling for `paginate()`. Measured: 1,041 sessions gives 6
 *  pages at 200/page. 50 pages is a 10,000-session ceiling, comfortably
 *  above any real athlete's lifetime volume, while still giving the walk a
 *  cap rather than none (HR-10's lesson: a per-user read needs a ceiling on
 *  the INPUT, not just a per-page limit). */
export const SESSIONS_ALL_MAX_PAGES = 50;

export const SESSIONS_ALL_PATH = "/api/sessions";

/** #509: a write to `/api/sessions`, the collection or one session by id (a
 *  query string allowed): its SessionResult is recomputed server-side. The one
 *  definition both hosts use to drop the Progress snapshot. */
export const isSessionWritePath = (path: string): boolean =>
  /^\/api\/sessions(\/|\?|$)/.test(path);

/** What the endpoint answers. Rows stay `unknown` until `Session.safeParse`
 *  has had a look (mirrors `MeasurementsListResponse`). */
export type SessionsListResponse = { items: unknown[]; cursor: string | null };

export function sessionsAllPagePath(cursor: string | null): string {
  return buildCursorPath(SESSIONS_ALL_PATH, `limit=${SESSIONS_ALL_PAGE_SIZE}`, cursor);
}

/** The per-platform seam: mobile and web each bring their own audited
 *  apiFetch. Concrete (not generic), same reasoning as measurementsPage.ts's
 *  PageFetcher: a generic function type can't be satisfied by a test
 *  double. */
export type SessionsPageFetcher = (
  path: string,
) => Promise<SessionsListResponse>;

/** Rows that fail the schema are DROPPED, not thrown on: one malformed
 *  historical row must not blank a whole page of an athlete's history.
 *  Shared by every reader of a `SessionsListResponse.items`, right now
 *  `makeSessionsAllFetchPage` below, mobile's `useSessionsListData`, and
 *  web-rnw's `useSessionsList` (HC-22/PG-38, CN-sessions-envelope-type),
 *  so "which rows count as a valid session" has one definition. */
export function parseSessionRows(items: unknown[]): Session[] {
  const out: Session[] = [];
  for (const raw of items) {
    const parsed = Session.safeParse(raw);
    if (parsed.success) out.push(parsed.data);
  }
  return out;
}

/** One row of `GET /api/sessions?include=sets`. */
export type SessionWithSets = { session: Session; sets: LogSet[] };

/** The `?include=sets` twin of parseSessionRows: a row whose session fails
 *  the schema is dropped, a set that fails it is dropped from its session. */
export function parseSessionWithSetsRows(items: unknown[]): SessionWithSets[] {
  const out: SessionWithSets[] = [];
  for (const raw of items) {
    const row = raw as { session?: unknown; sets?: unknown } | null;
    const parsed = Session.safeParse(row?.session);
    if (!parsed.success) continue;
    const sets: LogSet[] = [];
    for (const s of Array.isArray(row?.sets) ? row.sets : []) {
      const set = LogSet.safeParse(s);
      if (set.success) sets.push(set.data);
    }
    out.push({ session: parsed.data, sets });
  }
  return out;
}

/** web-rnw's hand-written parse loop already dropped malformed rows;
 *  mobile's single-page fetch didn't validate at all, so this was an
 *  incidental behaviour change there: mobile now also drops a malformed
 *  session row instead of keeping it unparsed. */
export function makeSessionsAllFetchPage(
  fetchJson: SessionsPageFetcher,
): FetchPage<Session, string> {
  return async (cursor) => {
    const res = await fetchJson(sessionsAllPagePath(cursor));
    return { items: parseSessionRows(res.items ?? []), cursor: res.cursor };
  };
}

/** What the totals read of a session row (#238): when it started, and its
 *  source, which says strength or cardio (#321). */
export type SessionStart = Pick<Session, "startedAt" | "source">;

/** #321: every Health Connect session is cardio, every other is strength (a
 *  strength session enriched with heart rate stays `manual`). */
export function isCardioSession(s: Pick<Session, "source">): boolean {
  return s.source === "healthconnect";
}

/** #419: the one test for "this session counts as strength training": not
 *  Health Connect, and at least one COUNTED set (`setCount > 0`; a cardio
 *  bout counts none, see setMetrics). ⛔ Not "has hard sets": imported
 *  StrengthLog history has no RIR, so its `hardSetCount` is 0. `source` is
 *  optional because a SessionResult row does not carry it. */
export function countsAsStrength(s: {
  source?: Session["source"];
  exerciseResults?: ReadonlyArray<{ setCount?: number }>;
}): boolean {
  if (s.source !== undefined && isCardioSession({ source: s.source })) return false;
  return (s.exerciseResults ?? []).some((e) => (e.setCount ?? 0) > 0);
}

/** PG-38's walk over every page, keeping only each row's start and source:
 *  the stats, the weekly bars and the streak count every session, and the
 *  rest of the row is dropped with its page. */
export async function readAllSessionStarts(
  fetchJson: SessionsPageFetcher,
  signal?: AbortLike,
): Promise<SessionStart[]> {
  const fetchPage = makeSessionsAllFetchPage(fetchJson);
  const { items } = await paginate<SessionStart>(
    async (cursor) => {
      const page = await fetchPage(cursor);
      return {
        items: page.items.map((s) => ({ startedAt: s.startedAt, source: s.source })),
        cursor: page.cursor,
      };
    },
    { maxPages: SESSIONS_ALL_MAX_PAGES, ...(signal ? { signal } : {}) },
  );
  return items;
}
